/**
 * Zod schemas — single source of truth for boundary validation.
 * Used by Fastify routes (`fastify-type-provider-zod`) AND by web forms
 * (`react-hook-form` + `zodResolver`).
 */
import { z } from 'zod';
import { ANALYSIS_STATUSES, DAM_SOURCE_MANUAL, DEYE_WINDOWS_PER_DAY, DEYE_WINDOW_MODES, INPUT_CT_VALUES, PHAGE_ACCESS_STATUSES, POTENTIAL_STATUSES, ROLES, SITE_MODES, STRATEGIES, TARIFF_TYPES, TIERS, VENDORS, } from './types.js';
// ── Primitives ─────────────────────────────────────────────────────────
export const emailSchema = z.string().trim().toLowerCase().email();
export const passwordSchema = z.string().min(8).max(128);
/** «Ім'я та прізвище». */
export const personNameSchema = z.string().trim().min(1).max(120);
/** Phone as typed, e.g. `+380 67 123 45 67`: digits, spaces, `+ - ( )`;
 *  9–15 digits (a local or an international number). */
export const phoneSchema = z
    .string()
    .trim()
    .regex(/^\+?[\d ()-]{5,32}$/, 'expected a phone number')
    .refine((v) => {
    const digits = v.replace(/\D/g, '').length;
    return digits >= 9 && digits <= 15;
}, 'expected a phone number');
/** ISO 3166-1 alpha-2, uppercase. */
export const countryCodeSchema = z
    .string()
    .length(2)
    .regex(/^[A-Z]{2}$/, 'expected ISO 3166-1 alpha-2 uppercase');
export const tierSchema = z.enum(TIERS);
export const roleSchema = z.enum(ROLES);
export const tariffTypeSchema = z.enum(TARIFF_TYPES);
export const inputCtSchema = z.enum(INPUT_CT_VALUES);
export const phageAccessStatusSchema = z.enum(PHAGE_ACCESS_STATUSES);
export const vendorSchema = z.enum(VENDORS);
export const siteModeSchema = z.enum(SITE_MODES);
export const strategySchema = z.enum(STRATEGIES);
/** A time zone the runtime knows (IANA, e.g. `Europe/Kyiv`): every plan is built in it. */
export function isIanaTimeZone(tz) {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}
/** YYYY-MM-DD calendar date in market timezone. */
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
// ── Auth ───────────────────────────────────────────────────────────────
export const signupBodySchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    country_code: countryCodeSchema,
    /** Release 1 consent (data processing + inverter control). Optional in the
     *  schema so older clients still parse; stored with `terms_version` and the time. */
    terms_accepted: z.literal(true).optional(),
    /** Version of the consent text the user accepted. */
    terms_version: z.string().min(1).max(40).optional(),
    /** Iteration 2 registration: name and phone, shown to the administrator.
     *  Optional in the schema so older clients still parse. */
    name: personNameSchema.optional(),
    phone: phoneSchema.optional(),
});
export const loginBodySchema = z.object({
    email: emailSchema,
    password: passwordSchema,
});
// ── Profile ────────────────────────────────────────────────────────────
export const publicProfileSchema = z.object({
    id: z.string().uuid(),
    email: emailSchema,
    tier: tierSchema,
    role: roleSchema,
    country_code: countryCodeSchema,
    default_market_id: z.string(),
    preferred_locale: z.string(),
    created_at: z.string(),
});
// ── Common error envelope returned by Fastify error handler. ───────────
export const errorEnvelopeSchema = z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
});
// ── Auth responses ─────────────────────────────────────────────────────
export const signupResponseSchema = z.object({
    user: publicProfileSchema,
});
export const loginResponseSchema = z.object({
    user: publicProfileSchema,
});
// ── Sites ──────────────────────────────────────────────────────────────
export const pvOrientationSchema = z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    tilt: z.number().min(0).max(90),
    azimuth: z.number().min(0).max(360),
});
export const credentialsSchema = z.record(z.string());
export const siteCreateBodySchema = z.object({
    name: z.string().min(1).max(120),
    vendor: vendorSchema,
    model: z.string().optional(),
    /** D5: the inverter's SN from its sticker, typed by the client; spaces dropped.
     *  No DeyeCloud login: the station is found once the client opens it to Phage.Energy. */
    device_sn: z
        .string()
        .transform((v) => v.replace(/\s+/g, ''))
        .pipe(z.string().min(1).max(64)),
    /** D6: not asked at connection — filled from DeyeCloud once access is open. */
    capacity_kwh: z.number().positive().optional(),
    p_max_kw: z.number().positive().optional(),
    soc_min_pct: z.number().min(0).max(100).default(15),
    soc_max_pct: z.number().min(0).max(100).default(95),
    eta_charge: z.number().min(0.5).max(1).default(0.95),
    eta_discharge: z.number().min(0.5).max(1).default(0.95),
    timezone: z.string().refine(isIanaTimeZone, 'unknown IANA time zone').optional(),
    market_id: z.string().optional(),
    mode: siteModeSchema.default('internal'),
    schedule_mode: z.enum(['manual', 'auto']).default('manual'),
    default_strategy: strategySchema.default('manual'),
    has_pv: z.boolean().default(false),
    pv_kw_peak: z.number().positive().optional(),
    pv_orientation: pvOrientationSchema.optional(),
    prefer_pv_charge: z.boolean().default(true),
    tariff_template_id: z.string().optional(),
    /** Release 1 object fields (package B7 maps them on create / update).
     *  No outage reserve here: it defaults to 20 % and is changed in the
     *  object's settings (owner, 20.10.2026). */
    tariff_type: tariffTypeSchema.optional(),
    region: z.string().min(1).max(80).optional(),
    osr_code: z.string().min(1).max(80).optional(),
    /** Current transformer at the input; absent → `unknown` (works as without). */
    input_ct: inputCtSchema.optional(),
});
export const siteUpdateBodySchema = siteCreateBodySchema
    .omit({ device_sn: true, vendor: true })
    .extend({ reserve_pct: z.number().min(0).max(100) })
    .partial();
export const tariffSourceSchema = z.enum(['template', 'dam']);
/**
 * D2: an inverter's TOU program as read from DeyeCloud (`config/tou`, the
 * work mode from `config/system` when it answers): the switch and the 6
 * windows — start, power, SOC, charge from the grid.
 */
export const inverterProgramSchema = z.object({
    /** «Програма за часом» on the inverter is on. */
    enabled: z.boolean(),
    /** DEYE work mode (`ZERO_EXPORT_TO_CT` …); null — `config/system` not read. */
    work_mode: z.string().nullable(),
    windows: z
        .array(z.object({
        /** Local start «HH:MM»; a window lasts until the next one's start, the last until midnight. */
        time: z.string(),
        power_w: z.number(),
        /** null in voltage mode. */
        soc_pct: z.number().nullable(),
        grid_charge: z.boolean(),
    }))
        .length(6),
});
export const paramSourceSchema = z.enum(['deye', 'manual']);
/**
 * Owner, 25.10.2026: a read of the inverter's settings showed a program other
 * than Phage's latest write — changed by hand; nothing is written to the
 * inverter until «Повернути програму Phage». `at` — when the read showed it,
 * `change` — what differs (as the adapter tells it).
 */
export const programChangedSchema = z.object({ at: z.string(), change: z.string().nullable() });
/**
 * Owner, 24.10.2026: the battery currents, A, as the plan takes them (the
 * latest read, else the original settings; Max A Charge / Discharge never
 * written by Phage, the grid charge current — for the program's grid
 * charge) and the plan's limits by them, kW. The object's screen and the
 * administrator's list.
 */
export const inverterLimitsViewSchema = z.object({
    charge_a: z.number().nullable(),
    discharge_a: z.number().nullable(),
    grid_charge_a: z.number().nullable(),
    /** The installer's grid charge current, A (the stored original settings): the program sets its own for a grid charge and puts it back. */
    grid_charge_a_original: z.number().nullable(),
    /** `read` — the latest read of the inverter's settings; `original` — the stored original settings; null — none. */
    source: z.enum(['read', 'original']).nullable(),
    at: z.string().nullable(),
    /** The installer's grid charge switch; null — not read. */
    grid_charge_on: z.boolean().nullable(),
    /** The battery voltage the currents are turned into kW with; null — not known (no current limit). */
    battery_v: z.number().nullable(),
    /** The currents on the inverter are the ones Phage wrote (22–23.10.2026), not the installer's. */
    by_phage: z.boolean(),
    /** The inverter's power. */
    rated_kw: z.number(),
    /** Any window at most, kW: the inverter's power within the discharge current and the installer's TOU windows. */
    power_kw: z.number(),
    /** A grid charge at most, kW; null — not known (`power_kw` only). */
    grid_charge_kw: z.number().nullable(),
});
export const siteResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    vendor: vendorSchema,
    model: z.string().nullable(),
    vendor_site_id: z.string(),
    /** D6: null until DeyeCloud (access open) or the client gives it. */
    capacity_kwh: z.number().nullable(),
    p_max_kw: z.number().nullable(),
    /** D10: allowed power of the connection point, kW (connection contract,
     *  «вручну»); null — no limit but the inverter's. */
    allowed_power_kw: z.number().nullable(),
    soc_min_pct: z.number(),
    soc_max_pct: z.number(),
    eta_charge: z.number(),
    eta_discharge: z.number(),
    timezone: z.string(),
    market_id: z.string(),
    /**
     * ISO 4217 from the site's market. Lets web fall back to the right
     * currency unit (₴ / € / zł …) when the DAM curve isn't fetched yet
     * — without it, every empty heatmap defaults to the wrong symbol on
     * UA sites. See TZ-OREE.md §1.1.
     */
    local_currency: z.string().length(3),
    mode: siteModeSchema,
    schedule_mode: z.enum(['manual', 'auto']),
    default_strategy: strategySchema,
    has_pv: z.boolean(),
    pv_kw_peak: z.number().nullable(),
    pv_orientation: pvOrientationSchema.nullable(),
    prefer_pv_charge: z.boolean(),
    /** Site has a stored 24-cell expected-load template. */
    has_expected_load: z.boolean(),
    /**
     * `template` = static 24-cell `tariff_profiles` (default for EU markets).
     * `dam` = effective tariff derived from DAM × correction (default for
     * UA group A sites). Drives the Settings UI variant.
     */
    tariff_source: tariffSourceSchema,
    /** Release 1 object fields. `tariff_type`, `region`, `osr_code` are NULL
     *  for objects created before release 1. */
    tariff_type: tariffTypeSchema.nullable(),
    region: z.string().nullable(),
    osr_code: z.string().nullable(),
    /** Zone rates of the object (two- / three-zone tariff) as the zone plan
     *  reads them: 24 wall-clock cells, currency per kWh (`PUT /sites/:id/tariff`);
     *  null until entered. */
    zone_rates_per_kwh: z.array(z.number()).length(24).nullable(),
    reserve_pct: z.number(),
    /** «Максимальний резерв» (owner, 23.10.2026). */
    max_reserve: z.boolean(),
    internal_arbitrage_enabled: z.boolean(),
    /** Iteration 2 object fields. */
    input_ct: inputCtSchema,
    /** DeyeCloud has not answered the live poll (or answered an error) since
     *  then — «DeyeCloud не відповідає з 14:40»; null — it answers. Not the
     *  inverter being offline (then DeyeCloud answers its old data). */
    vendor_down_since: z.string().nullable(),
    /** Inverter settings saved at connection (raw DEYE `config/system` and
     *  `config/tou`); null until read. */
    original_settings: z.object({ saved_at: z.string(), system: z.unknown(), tou: z.unknown() }).nullable(),
    /**
     * D2: the inverter's settings as last read («Розклад в інверторі зараз»,
     * «Оновити з інвертора»). `read_at` — the last read; `program` — null when
     * not read or refused; `unreadable` — DeyeCloud refuses to read the
     * settings, the object is not managed (internal arbitrage can't be switched
     * on); `next_read_at` — «Оновити з інвертора» is possible from then on
     * (once in 10 minutes), null — now.
     */
    inverter_settings: z.object({
        read_at: z.string().nullable(),
        program: inverterProgramSchema.nullable(),
        unreadable: z.string().nullable(),
        next_read_at: z.string().nullable(),
        /**
         * D9: the battery currents in the inverter's settings, A
         * (`strategy/dynamicControl/read`); null — not read. A 0 A charge or
         * discharge current — the battery does not charge (discharge).
         */
        battery_currents: z
            .object({
            charge_a: z.number().nullable(),
            discharge_a: z.number().nullable(),
            grid_charge_a: z.number().nullable(),
        })
            .nullable(),
        /**
         * Owner, 24.10.2026: the currents on the inverter are the ones Phage wrote
         * (22–23.10.2026), not the installer's — no grid charge until the
         * installer sets them; Phage never writes them.
         */
        currents_by_phage: z.boolean(),
        /** The installer's limits as the plan takes them: the currents, the switch, the battery voltage, the plan's kW. */
        limits: inverterLimitsViewSchema,
        /** The program was changed by hand on the inverter; null — it runs Phage's (or its own settings). */
        program_changed: programChangedSchema.nullable(),
    }),
    /** Manual edit of today's windows is kept up to and including this local day. */
    manual_windows_until: isoDateSchema.nullable(),
    /**
     * D3/D4: access of Phage.Energy to the station in DeyeCloud — the object's
     * data and control go through it. `checked_at` — DeyeCloud last answered a
     * check (null — never); `next_check_at` — «Перевірити доступ» is possible
     * from then on (once a minute), null — now.
     */
    phage_access: z.object({
        status: phageAccessStatusSchema,
        checked_at: z.string().nullable(),
        next_check_at: z.string().nullable(),
    }),
    /** D6: where a parameter came from — «з DeyeCloud» (`deye`) or «вручну» (`manual`); absent — not set. */
    params_source: z.record(paramSourceSchema),
    created_at: z.string(),
});
/**
 * `GET /phage-access` (D3): what the instruction «Доступ для Phage.Energy»
 * shows — the business name clients look up in DeyeCloud (null — not known:
 * not set and no sign-in yet), and whether «Перевірити доступ» can work
 * (false — the business account is not set on the server: «перевірка недоступна»).
 */
export const phageAccessInfoResponseSchema = z.object({
    check_available: z.boolean(),
    company_name: z.string().nullable(),
});
/**
 * `PATCH /sites/:id/settings` — «Налаштування» of the object: owner or
 * administrator (the administrator's change goes to the object's journal).
 * Only the fields sent change. The outage reserve is set here only. Zone
 * rates of a zone tariff go through `PUT /sites/:id/tariff` (24 cells).
 */
export const siteSettingsBodySchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    capacity_kwh: z.number().positive(),
    p_max_kw: z.number().positive(),
    /** D10: null clears it — no limit but the inverter's. */
    allowed_power_kw: z.number().positive().nullable(),
    soc_min_pct: z.number().min(0).max(100),
    soc_max_pct: z.number().min(0).max(100),
    reserve_pct: z.number().min(0).max(100),
    /** «Максимальний резерв»: the battery kept full, never discharged by the program. */
    max_reserve: z.boolean(),
    input_ct: inputCtSchema,
    region: z.string().min(1).max(80),
    /** null clears it (a region without a distribution operator of ours). */
    osr_code: z.string().min(1).max(80).nullable(),
    tariff_type: tariffTypeSchema,
})
    .partial()
    .refine((b) => Object.keys(b).length > 0, 'nothing to change')
    .refine((b) => b.soc_min_pct === undefined || b.soc_max_pct === undefined || b.soc_min_pct < b.soc_max_pct, { message: 'soc_min_pct must be below soc_max_pct', path: ['soc_min_pct'] });
/**
 * Per-site expected hourly load profile for internal-mode P&L (avoided cost).
 * The DB stores a 24-cell wall-clock template; the API expands it to the
 * real day length (23/24/25) when `?date=` is supplied. Returned `null`
 * when no profile is set — the savings simulator falls back to assuming
 * zero baseline (hero shows 0 savings rather than guessing).
 *
 * Stage P: `source` discriminates how the values were derived so the UI
 * can label the savings card honestly.
 *   - `telemetry`     — median p_load_kw aggregated from `telemetry`
 *                        over `coverage_days` recent calendar days.
 *   - `manual`        — the wall-clock template the user typed in
 *                        Settings → Expected load (or the wizard's
 *                        evenly-distributed daily-kWh seed).
 *   - `none` (returned with `null` payload) — no data either way.
 *
 * `coverage_days` is the count of distinct calendar days that contributed
 * to the telemetry aggregate, capped at 30. Useful for the UI to show
 * «по N дням» honesty badge.  Always `null` for `manual`.
 */
export const expectedLoadResponseSchema = z.object({
    site_id: z.string().uuid(),
    hourly_kwh: z.array(z.number()),
    source: z.enum(['telemetry', 'manual']),
    coverage_days: z.number().int().nonnegative().nullable(),
    /** D2: spread of the history's days per wall-clock hour (24), [10 %, 90 %]
     *  of the same hourly values as the automatic profile, kWh; null — no data
     *  for the hour. */
    spread_kwh: z.array(z.tuple([z.number(), z.number()]).nullable()).length(24),
});
/**
 * Per-site retail tariff profile for internal-mode strategies. Length must
 * match the day's real hours (23/24/25). Returned `null` when no profile
 * is configured — internal-mode P&L falls back to zero savings in that case.
 *
 * Returned by `GET /api/sites/:id/tariff?date=...` for both `tariff_source`
 * variants:
 *   - `template` — values come from the static 24-cell `tariff_profiles`
 *     row, expanded to the day's DST length.
 *   - `dam` — values are derived on the fly from `dam_prices` for the
 *     site's market × the correction active on `date`. `updated_at`
 *     reflects the active correction's `created_at` (or now() if no
 *     correction was ever set, in which case the effective tariff is
 *     just the raw DAM curve in local currency / kWh).
 */
export const tariffProfileResponseSchema = z.object({
    site_id: z.string().uuid(),
    source: tariffSourceSchema,
    hourly_local_per_kwh: z.array(z.number()),
    updated_at: z.string(),
});
/**
 * One row of the per-site DAM-correction history. New corrections are
 * inserted as new rows with future-or-current `effective_from`; past
 * rows are immutable. The `MAX(effective_from) WHERE effective_from <= D`
 * row applies to delivery date D — carry-forward is automatic.
 */
export const tariffCorrectionResponseSchema = z.object({
    site_id: z.string().uuid(),
    effective_from: isoDateSchema,
    correction_pct: z.number(),
    correction_fixed_per_mwh: z.number(),
    created_at: z.string(),
});
export const tariffCorrectionUpsertBodySchema = z.object({
    effective_from: isoDateSchema,
    correction_pct: z.number().min(-100).max(1000),
    correction_fixed_per_mwh: z.number(),
});
// ── Schedules ──────────────────────────────────────────────────────────
export const scheduleStatusSchema = z.enum(['draft', 'approved', 'cancelled']);
export const scheduleResponseSchema = z.object({
    id: z.string().uuid(),
    site_id: z.string().uuid(),
    date: isoDateSchema,
    source: z.string(),
    mode: siteModeSchema,
    hourly_setpoints_kw: z.array(z.number()),
    status: scheduleStatusSchema,
    created_at: z.string(),
    approved_at: z.string().nullable(),
    /** Stage J: ISO date of the source plan if this row was created via
     *  carry-forward by the dispatcher; null for user-applied or
     *  optimizer-built schedules. */
    carryforward_source_date: isoDateSchema.nullable(),
});
export const scheduleUpsertBodySchema = z.object({
    site_id: z.string().uuid(),
    date: isoDateSchema,
    mode: siteModeSchema,
    hourly_setpoints_kw: z.array(z.number()),
    source: z.string().optional(),
});
export const scheduleBuildBodySchema = z.object({
    site_id: z.string().uuid(),
    date: isoDateSchema,
    strategy: strategySchema,
    mode: siteModeSchema,
});
export const scheduleBuildResponseSchema = z.object({
    hourly_setpoints_kw: z.array(z.number()),
});
// ── Metadata (onboarding) ──────────────────────────────────────────────
export const vendorModelMetaSchema = z.object({
    vendor: vendorSchema,
    model: z.string(),
    capacity_range: z.string().nullable(),
    pmax_range: z.string().nullable(),
    supports_tou_batch: z.boolean(),
    supports_feed_in: z.boolean(),
});
export const tariffTemplateMetaSchema = z.object({
    id: z.string(),
    name: z.string(),
    hourly_local_per_kwh: z.array(z.number()).length(24),
});
export const countryMetaSchema = z.object({
    code: z.string().length(2),
    name: z.string(),
    default_market_id: z.string(),
});
export const marketMetaSchema = z.object({
    id: z.string(),
    country_code: z.string().length(2),
    name: z.string(),
    local_currency: z.string().length(3),
    timezone: z.string(),
});
// ── Execution log ──────────────────────────────────────────────────────
/**
 * One TOU window as persisted on `execution_log.tou_slots[*]`. Mirrors
 * the canonical `TouSlot` interface in `types.ts` — kept in sync by
 * hand because Zod can't infer from the TS type and we want runtime
 * validation on the API boundary.
 */
export const touSlotSchema = z.object({
    start_utc: z.string(),
    end_utc: z.string(),
    action: z.enum(['charge', 'discharge_to_grid', 'hold', 'self_consumption']),
    power_kw: z.number(),
    target_soc_pct: z.number().optional(),
    min_soc_pct: z.number().optional(),
    charge_from_grid: z.boolean().optional(),
});
export const executionLogStatusSchema = z.enum(['sent', 'failed', 'skipped', 'cancelled']);
/**
 * Last execution-log row per (site, date). The DB uniqueness on
 * (site_id, date) means at most one row exists, so the response is
 * `{ execution_log: ExecutionLogResponse | null }` rather than an array.
 *
 * `inputs_snapshot` is opaque on the contract — its shape is owned by
 * the dispatcher and consumed by the HourInspector inputs panel. A
 * permissive `unknown` keeps the wire stable while the snapshot schema
 * solidifies through Day 5 work.
 */
export const executionLogResponseSchema = z.object({
    id: z.string().uuid(),
    site_id: z.string().uuid(),
    schedule_id: z.string().uuid().nullable(),
    date: isoDateSchema,
    uploaded_at: z.string(),
    tou_slots: z.array(touSlotSchema),
    tou_hash: z.string(),
    status: executionLogStatusSchema,
    vendor_message_id: z.string().nullable(),
    error: z.string().nullable(),
    inputs_snapshot: z.unknown().nullable(),
});
// ── History / archive ──────────────────────────────────────────────────
/**
 * Per-hour telemetry aggregate, derived via TimescaleDB
 * `time_bucket('1 hour', ts)`. Each numeric field is the bucket mean
 * (or `null` if no rows landed in the bucket — site offline that hour).
 *
 * Sign convention preserved as in the raw `telemetry` row (vendor): for
 * `p_battery_kw`, `+` = charge / `−` = discharge. Consumers comparing
 * against `schedules.hourly_setpoints_kw` (which uses the inverted
 * schedule convention `+` = discharge) must flip the sign.
 */
export const telemetryHourlyBucketSchema = z.object({
    bucket_utc: z.string(),
    p_battery_kw: z.number().nullable(),
    p_grid_kw: z.number().nullable(),
    p_pv_kw: z.number().nullable(),
    p_load_kw: z.number().nullable(),
    soc_pct: z.number().nullable(),
    /** Energy into the battery that hour, kWh = mean charging power × 1h
     *  (integral of the sampled power; `null` if no samples). */
    charge_kwh: z.number().nullable(),
    /** Energy out of the battery that hour, kWh = mean discharging power × 1h. */
    discharge_kwh: z.number().nullable(),
});
export const telemetryHourlyResponseSchema = z.object({
    site_id: z.string().uuid(),
    from: isoDateSchema,
    to: isoDateSchema,
    buckets: z.array(telemetryHourlyBucketSchema),
});
/**
 * One row of `GET /api/history?site_id&from&to` — plan-vs-fact aggregate
 * for a single calendar day in the site's timezone.
 *
 * Battery-attributable accounting (TZ.md §Day 5; ARCHITECTURE.md §8.6):
 *   - `plan_kwh_*` come from `schedules.hourly_setpoints_kw` (signed
 *     per schedule canon: + = discharge, − = charge → split into the
 *     two non-negative kWh totals here).
 *   - `fact_kwh_*` come from `AVG(p_battery_kw)` over each hour
 *     bucket; the schedule-flip (`-avg`) is applied so the totals
 *     match the schedule convention.
 *   - `expected_pnl` / `actual_pnl` are computed via the canonical
 *     `pnl.ts` helpers — `expectedExternalMargin` /
 *     `expectedInternalSavings` for plan-time; `actualExternalMargin`
 *     / `actualInternalSavings` for archive-time.
 *
 * `null` semantics: `actual_*` is null if no telemetry landed that day
 * (site offline / brand-new install). `expected_pnl` is null only when
 * the inputs needed by the chosen pnl helper are missing for that day
 * (e.g. internal mode without a tariff resolved).
 */
export const historyDayRowSchema = z.object({
    date: isoDateSchema,
    mode: siteModeSchema,
    expected_pnl: z.number().nullable(),
    actual_pnl: z.number().nullable(),
    plan_kwh_charge: z.number(),
    plan_kwh_discharge: z.number(),
    fact_kwh_charge: z.number().nullable(),
    fact_kwh_discharge: z.number().nullable(),
});
export const historyResponseSchema = z.object({
    site_id: z.string().uuid(),
    from: isoDateSchema,
    to: isoDateSchema,
    /** ISO 4217 of the site's market — drives the UI's `₴ / € / zł` label. */
    currency: z.string().length(3),
    days: z.array(historyDayRowSchema),
});
// ── Object analysis (release 1) ────────────────────────────────────────
/** Energy totals of a period, kWh. */
export const energyTotalsSchema = z.object({
    load_kwh: z.number(),
    pv_kwh: z.number(),
    grid_import_kwh: z.number(),
    grid_export_kwh: z.number(),
    charge_kwh: z.number(),
    discharge_kwh: z.number(),
});
/** `GET /sites/:id/analysis` — history download progress + statistics. */
export const siteAnalysisResponseSchema = z.object({
    site_id: z.string().uuid(),
    status: z.enum(ANALYSIS_STATUSES),
    windows_done: z.number().int(),
    windows_total: z.number().int(),
    error: z.string().nullable(),
    started_at: z.string().nullable(),
    finished_at: z.string().nullable(),
    last_day_loaded: isoDateSchema.nullable(),
    /**
     * Last complete local day (every hour with the grid import and export and
     * each counter the object reports known — audit 22.10.2026 p.7); NULL
     * until the history is there. `restored_hours` — hours whose energies were
     * spread over a gap between samples, not measured within the hour.
     */
    last_day: z
        .object({ date: isoDateSchema, totals: energyTotalsSchema, restored_hours: z.number().int() })
        .nullable(),
    /** Totals over the complete local days of the last 30, and how many they are. */
    last_30_days: z
        .object({ days: z.number().int(), totals: energyTotalsSchema, restored_hours: z.number().int() })
        .nullable(),
});
/**
 * D11 «Гроші» (`GET /sites/:id/money`): the same complete days as
 * `last_30_days` (`restored_hours` — as there); days of a DAM tariff without prices are left out of both
 * sums. `no_tariff` — no tariff or no zone rates: no sums; `no_history` — no
 * complete day. A fixed tariff without its rate has no sums but the difference 0.
 */
export const siteMoneyResponseSchema = z.object({
    site_id: z.string().uuid(),
    /** `no_prices` — a DAM tariff without real DAM prices for the days of the history. */
    status: z.enum(['ok', 'no_tariff', 'no_history', 'no_prices']),
    tariff_type: tariffTypeSchema.nullable(),
    days: z.number().int(),
    from: isoDateSchema.nullable(),
    to: isoDateSchema.nullable(),
    /** Bought from the grid over the days, kWh. */
    import_kwh: z.number().nullable(),
    /** Витрати на електрику, currency. */
    actual: z.number().nullable(),
    /** З внутрішнім арбітражем, розрахунок, currency. */
    with_arbitrage: z.number().nullable(),
    /** Різниця = actual − with_arbitrage. */
    difference: z.number().nullable(),
    /** Zone / fixed tariff: bought kWh by rate, dearest first. */
    by_rate: z.array(z.object({ rate: z.number(), kwh: z.number() })),
    /** DAM tariff: mean price of the bought kWh, currency per kWh. */
    avg_price: z.number().nullable(),
    /** Економія від СЕС — objects with PV only; null otherwise. */
    pv: z
        .object({
        produced_kwh: z.number(),
        /** Exported out of the production (each hour at most what PV produced). */
        exported_kwh: z.number(),
        /** Used on site = produced − exported. */
        self_kwh: z.number(),
        /** self × the hour price, currency; null — the fixed rate is not set. */
        saving: z.number().nullable(),
        /** Zone / fixed tariff: used-on-site kWh by rate, dearest first. */
        by_rate: z.array(z.object({ rate: z.number(), kwh: z.number() })),
        /** DAM tariff: mean price of the used-on-site kWh. */
        avg_price: z.number().nullable(),
    })
        .nullable(),
    /** Hours of the days whose energies were spread over a gap between samples. */
    restored_hours: z.number().int(),
});
/** VPP participation potential (TZ «Расчёт»), latest computation. */
export const vppPotentialResponseSchema = z.object({
    site_id: z.string().uuid(),
    status: z.enum(POTENTIAL_STATUSES),
    window_days: z.number().int(),
    days_used: z.number().int(),
    /** Increment over the window, local currency. NULL unless status `ok`. */
    increment_window: z.number().nullable(),
    /** `increment_window × 365 / days_used`. NULL unless status `ok`. */
    increment_year: z.number().nullable(),
    computed_at: z.string(),
    version: z.string(),
    /** For `no_data`: `no_profile_and_prices` or `load_above_inverter_rating`. */
    reason: z.string().nullable(),
});
/** `GET /sites/:id/potential` — null until the first computation. */
export const sitePotentialResponseSchema = z.object({ potential: vppPotentialResponseSchema.nullable() });
/** `GET /sites/:id/overview` — object screen header and current state (owner or operator). */
export const siteOverviewResponseSchema = z.object({
    site: z.object({
        id: z.string().uuid(),
        name: z.string(),
        timezone: z.string(),
        local_currency: z.string().length(3),
        capacity_kwh: z.number(),
        p_max_kw: z.number(),
        soc_min_pct: z.number(),
        soc_max_pct: z.number(),
        reserve_pct: z.number(),
        mode: siteModeSchema,
        internal_arbitrage_enabled: z.boolean(),
        tariff_type: tariffTypeSchema.nullable(),
        region: z.string().nullable(),
        osr_code: z.string().nullable(),
        input_ct: inputCtSchema,
        /** D4: access of Phage.Energy to the station — data and control go through it. */
        phage_access: phageAccessStatusSchema,
        /** DeyeCloud has not answered the live poll (or answered an error) since
         *  then — «DeyeCloud не відповідає з 14:40»; null — it answers. Not the
         *  inverter being offline (then DeyeCloud answers its old data). */
        vendor_down_since: z.string().nullable(),
        manual_windows_until: isoDateSchema.nullable(),
    }),
    /** The viewer owns the object (only the owner applies to the VPP). */
    is_owner: z.boolean(),
    /** Latest polled telemetry; `p_battery_kw` + charge / − discharge (vendor canon). */
    latest: z
        .object({
        ts: z.string(),
        soc_pct: z.number().nullable(),
        p_battery_kw: z.number().nullable(),
        p_grid_kw: z.number().nullable(),
        p_pv_kw: z.number().nullable(),
        p_load_kw: z.number().nullable(),
    })
        .nullable(),
});
/** `GET /sites/:id/day?date` — schedule and actual battery work of a local day. */
export const siteDayResponseSchema = z.object({
    date: isoDateSchema,
    /** Approved plan, kW per hour: + discharge / − charge; null without a plan. */
    plan_kw: z.array(z.number()).nullable(),
    plan_source: z.string().nullable(),
    /** Actual battery power, hourly mean of polled telemetry, kW: + discharge /
     *  − charge (same sign as the plan); null for hours without telemetry. */
    actual_kw: z.array(z.number().nullable()),
    /** Last SOC of each hour, %. */
    soc_pct: z.array(z.number().nullable()),
    /** Delivery day of the DAM prices the plan was built on: `date` itself
     *  («ціни РДН на …»), an earlier day while the day's prices are not in
     *  yet («ціни … — ціни доби ще не отримано»); null — no plan or not on
     *  DAM prices (zone / fixed tariff). */
    plan_prices_date: isoDateSchema.nullable(),
});
// ── Deye windows of a day (iteration 2) ────────────────────────────────
/** A real calendar date (`2026-02-30` is refused, not a 500). */
export const calendarDateSchema = isoDateSchema.refine((d) => {
    const t = Date.parse(`${d}T00:00:00Z`);
    return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === d;
}, 'expected a calendar date');
/** First hour of a window: position in the object's local day (0 … 22/23/24
 *  on a 23/24/25-hour day), step 1 hour. A window lasts until the next
 *  window's start, the last one until the end of the day. */
const windowStartSchema = z.number().int().min(0).max(24);
/** SOC threshold of a window, %. */
const windowSocSchema = z.number().min(0).max(100);
export const deyeWindowModeSchema = z.enum(DEYE_WINDOW_MODES);
/**
 * One Deye TOU window by mode:
 *   `grid_charge` — charge from the grid at `power_kw` up to `soc_pct`;
 *   `self_consumption` — discharge into the object's load at most `power_kw`,
 *     not below `soc_pct` (never below the object's SOC min);
 *   `hold` — no discharge (PV still charges): no power, no SOC threshold.
 */
export const deyeWindowSchema = z.discriminatedUnion('mode', [
    z.object({
        start_hour: windowStartSchema,
        mode: z.literal('grid_charge'),
        power_kw: z.number().finite().positive(),
        soc_pct: windowSocSchema,
    }),
    z.object({
        start_hour: windowStartSchema,
        mode: z.literal('self_consumption'),
        power_kw: z.number().finite().positive(),
        soc_pct: windowSocSchema,
    }),
    z.object({
        start_hour: windowStartSchema,
        mode: z.literal('hold'),
        power_kw: z.literal(0),
        soc_pct: z.null(),
    }),
]);
/** The day's windows: exactly 6, the first from 00:00, starts strictly
 *  increasing (each window ≥ 1 hour). Against the day itself (its length,
 *  the repeated hour of the clock change) — `dayWindowsProblem`. */
export const deyeDayWindowsSchema = z
    .array(deyeWindowSchema)
    .length(DEYE_WINDOWS_PER_DAY)
    .refine((ws) => ws[0]?.start_hour === 0, 'the first window starts at 00:00')
    .refine((ws) => ws.every((w, i) => i === 0 || w.start_hour > (ws[i - 1]?.start_hour ?? 0)), 'windows go in order, each at least 1 hour');
/**
 * `GET /sites/:id/windows?date` (owner or administrator; default today).
 * Editing rules with E = `elapsed_hours`: a window ending at or before E has
 * elapsed and does not change; a window starting at or before E keeps its
 * start (the one in progress may change mode, power and SOC); new or moved
 * starts are after E.
 */
export const siteWindowsResponseSchema = z.object({
    date: isoDateSchema,
    /** Day length, hours: 23 / 24 / 25. */
    hours: z.number().int().min(23).max(25),
    /** The day's 6 windows; null — no plan with windows: arbitrage off, fixed
     *  tariff or not built yet (the inverter runs its original settings). */
    windows: z.array(deyeWindowSchema).nullable(),
    /** How this day's windows were set: by the plan or by hand (kept by
     *  re-planning through `manual_until`). Null without windows. */
    source: z.enum(['auto', 'manual']).nullable(),
    /** When the windows were last set — re-planned or edited («перераховано о 14:00»). */
    updated_at: z.string().nullable(),
    /** The object's `manual_windows_until`. */
    manual_until: isoDateSchema.nullable(),
    /** Fully elapsed hours of the day (`elapsedHoursOfDay`): today — the
     *  position of the hour in progress, a past day — `hours`, a future one — 0. */
    elapsed_hours: z.number().int().min(0).max(25),
    /** As `SiteDayResponse.plan_prices_date`. */
    prices_date: isoDateSchema.nullable(),
    /**
     * Owner, 24.10.2026: the limits of a window, the plan's and an edit's alike
     * (`services/window-limits.ts`), checked when the windows are saved.
     */
    limits: z.object({
        /** Any window at most, kW: the inverter's power within the installer's discharge current and TOU windows. */
        p_max_kw: z.number(),
        /**
         * Grid charge at most per hour position, kW: the installer's charge
         * currents and the allowed power less the expected load (D10); null —
         * `p_max_kw` only.
         */
        charge_cap_kw: z.array(z.number()).nullable(),
        /** The battery currents on the inverter are Phage's (22–23.10.2026), not the installer's: no grid charge. */
        currents_by_phage: z.boolean(),
        soc_min_pct: z.number(),
        /** A discharge stops no lower: max(SOC min, outage reserve); SOC max under «Максимальний резерв». */
        soc_floor_pct: z.number(),
        soc_max_pct: z.number(),
    }),
});
/**
 * `PUT /sites/:id/windows` — manual edit of today's windows (drag or table),
 * owner or administrator (journal `windows`). Only today: windows are tied to
 * the time of day, tomorrow's are never written in advance. The editing rules
 * of `SiteWindowsResponse`, `dayWindowsProblem`, power ≤ the inverter's,
 * SOC within the object's SOC min … max → 422. No plan to edit (arbitrage
 * off, fixed tariff) → 409. Sets the day's windows `manual` and
 * `manual_windows_until` = `date`: the hourly re-plan keeps the edit to the
 * end of the day; the day's hourly plan follows the windows. Written to the
 * inverter. Answer — the windows as stored.
 */
export const siteWindowsBodySchema = z.object({
    date: calendarDateSchema,
    windows: deyeDayWindowsSchema,
});
/** `PATCH /sites/:id/internal-arbitrage` (package B5). */
export const internalArbitrageBodySchema = z.object({ enabled: z.boolean() });
// ── VPP applications (iteration 2) ─────────────────────────────────────
/** The client's application to connect the object to the VPP. `potential` —
 *  the 30-day potential at the moment of applying (snapshot; null if none). */
export const vppApplicationSchema = z.object({
    id: z.string().uuid(),
    site_id: z.string().uuid(),
    created_at: z.string(),
    potential: vppPotentialResponseSchema.nullable(),
});
/** `GET /sites/:id/application` (owner or operator); `POST` → 201 the same
 *  shape, 409 when the object already has one. */
export const siteApplicationResponseSchema = z.object({ application: vppApplicationSchema.nullable() });
// ── Password reset (iteration 2) ───────────────────────────────────────
/** `POST /auth/password-reset/request` — the answer is the same for any email. */
export const passwordResetRequestBodySchema = z.object({ email: emailSchema });
/** `POST /auth/password-reset/confirm` — token from the link + the new password.
 *  Used or expired token → 410 `reset_link_expired`. */
export const passwordResetConfirmBodySchema = z.object({
    token: z.string().min(1).max(256),
    password: passwordSchema,
});
export const okResponseSchema = z.object({ ok: z.literal(true) });
// ── Administrator (iteration 2; role `operator`) ───────────────────────
/** Client of an object as the administrator sees it. Name and phone are NULL
 *  for accounts registered before iteration 2. */
export const adminClientSchema = z.object({
    id: z.string().uuid(),
    email: z.string(),
    name: z.string().nullable(),
    phone: z.string().nullable(),
});
/** One row of «Об'єкти» — objects of all clients. */
export const adminSiteRowSchema = z.object({
    site_id: z.string().uuid(),
    site_name: z.string(),
    model: z.string().nullable(),
    /** Inverter serial number (DEYE `vendor_site_id` = `${stationId}:${deviceSn}`). */
    device_sn: z.string(),
    region: z.string().nullable(),
    client: adminClientSchema,
    /** Time of the latest telemetry sample («зв'язок»); null — never polled. */
    last_seen_at: z.string().nullable(),
    soc_pct: z.number().nullable(),
    /** «DeyeCloud не відповідає з …»; null — DeyeCloud answers. */
    vendor_down_since: z.string().nullable(),
    analysis: z
        .object({
        status: z.enum(ANALYSIS_STATUSES),
        windows_done: z.number().int(),
        windows_total: z.number().int(),
    })
        .nullable(),
    internal_arbitrage_enabled: z.boolean(),
    /** D3: access of Phage.Energy to the station; `checked_at` — the last answered check. */
    phage_access: z.object({ status: phageAccessStatusSchema, checked_at: z.string().nullable() }),
    /** Date of the VPP application; null — none. */
    application_created_at: z.string().nullable(),
    /** Owner, 24.10.2026: the battery currents and the plan's limits by them (`inverterLimitsViewSchema`). */
    currents: inverterLimitsViewSchema,
    /** Owner, 25.10.2026: the program was changed by hand on the inverter — nothing written. */
    program_changed: programChangedSchema.nullable(),
    created_at: z.string(),
});
/** `GET /admin/sites`. */
export const adminSitesResponseSchema = z.object({ sites: z.array(adminSiteRowSchema) });
/** One row of «Заявки». */
export const adminApplicationRowSchema = z.object({
    id: z.string().uuid(),
    created_at: z.string(),
    client: adminClientSchema,
    site: z.object({
        id: z.string().uuid(),
        name: z.string(),
        device_sn: z.string(),
        region: z.string().nullable(),
    }),
    /** The 30-day potential at the moment of applying; null if none was computed. */
    potential: vppPotentialResponseSchema.nullable(),
});
/** `GET /admin/applications`, newest first. */
export const adminApplicationsResponseSchema = z.object({ applications: z.array(adminApplicationRowSchema) });
/** `GET /admin/dam-prices` — which day the form takes and how many hours it has. */
export const adminDamPricesTargetResponseSchema = z.object({
    market_id: z.string(),
    /** Tomorrow in the market's timezone. */
    date: isoDateSchema,
    /** 24; 23 or 25 on a clock-change day (25.10.2026 — 25). */
    hours: z.number().int().min(23).max(25),
});
/** `PUT /admin/dam-prices` — hourly prices pasted from the market operator's
 *  site, ₴/MWh, hour by hour. The server checks the count against the day
 *  (422 `wrong_hour_count`) and stores them with source `manual`. */
export const adminDamPricesBodySchema = z.object({
    market_id: z.string(),
    date: isoDateSchema,
    hourly_local_per_mwh: z.array(z.number().finite().nonnegative()).min(23).max(25),
});
export const adminDamPricesResponseSchema = z.object({
    market_id: z.string(),
    date: isoDateSchema,
    hours: z.number().int(),
    source: z.literal(DAM_SOURCE_MANUAL),
    entered_at: z.string(),
});
//# sourceMappingURL=schemas.js.map